import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useI18n } from '../i18n/context';
import ListToolbar from '../ui/ListToolbar';
import '../styles/ui.css';
import './ListPages.css';

function toDateInputValue(d) {
  return d.toISOString().slice(0, 10);
}

// Unión de valores reales vistos en notification_logs.type (backend) y
// notification_client_events.notif_type (cliente) — los vocabularios no
// coinciden 1:1 entre sí (ver comentario en notificationDiagnostics.js),
// por eso se listan todos juntos en un único selector.
const NOTIF_TYPES = [
  'auto_break_started',
  'break_end',
  'break_end_registered',
  'break_end_reminder',
  'break_schedule_updated',
  'break_start_reminder',
  'chat',
  'clock_out',
  'exit_registered',
  'exit_reminder',
  'late',
  'new_task',
  'org_notification_settings_updated',
  'private_chat',
  'shift_ended',
  'shift_started',
  'task_status',
  'time_entry',
  'work_schedule_updated',
];

// Por defecto se muestran solo las últimas 48h — con miles de eventos
// acumulados en el historial, listar todo sin filtro es inmanejable.
function defaultDateRange() {
  const today = new Date();
  const twoDaysAgo = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);
  return { startDate: toDateInputValue(twoDaysAgo), endDate: toDateInputValue(today) };
}

export default function NotificationDiagnostics() {
  const { t, formatDate, formatDateTime } = useI18n();
  const defaults = defaultDateRange();
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState('');
  const [dedupeKey, setDedupeKey] = useState('');
  const [notifType, setNotifType] = useState('');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);

  function fetchPage(pageToLoad) {
    setLoading(true);
    const params = new URLSearchParams({ page: pageToLoad, limit: 50 });
    if (userId) params.set('userId', userId);
    if (dedupeKey) params.set('dedupeKey', dedupeKey);
    if (notifType) params.set('notifType', notifType);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    api(`/superadmin/notification-diagnostics?${params}`)
      .then((res) => {
        setGroups(res.data || []);
        setPagination(res.pagination || { page: 1, limit: 50, total: 0 });
      })
      .catch(() => {
        setGroups([]);
        setPagination({ page: 1, limit: 50, total: 0 });
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, startDate, endDate]);

  // Filtros de texto: aplicar solos mientras se escribe (con debounce), igual
  // que el resto del panel — antes solo se aplicaban al hacer clic en "Buscar",
  // lo que daba la impresión de que no funcionaban.
  const skipNextTextFilterFetch = useRef(true);
  useEffect(() => {
    if (skipNextTextFilterFetch.current) {
      skipNextTextFilterFetch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchPage(1);
      } else {
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dedupeKey, notifType]);

  function handleSearch() {
    if (page === 1) {
      fetchPage(1);
    } else {
      setPage(1);
    }
  }

  function handleClearFilters() {
    setUserId('');
    setDedupeKey('');
    setNotifType('');
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 50)));

  // Encabezado de día: como el backend ya devuelve los grupos ordenados por
  // recencia, alcanza con detectar el cambio de día al recorrer la lista.
  function groupTimestamp(g) {
    return g.backendEvents?.[0]?.sentAt || g.clientEvents?.[0]?.receivedAt || null;
  }
  const dayKeyFor = (g) => {
    const ts = groupTimestamp(g);
    return ts ? new Date(ts).toDateString() : t('notifDiagnostics.unknownDate');
  };
  const groupsWithDayHeaders = useMemo(() => {
    const out = [];
    let lastDayKey = null;
    for (const g of groups) {
      const dayKey = dayKeyFor(g);
      if (dayKey !== lastDayKey) {
        out.push({ isDayHeader: true, dayKey, label: formatDate(groupTimestamp(g)) || t('notifDiagnostics.unknownDate') });
        lastDayKey = dayKey;
      }
      out.push(g);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('notifDiagnostics.title')}</h1>
        <p className="sa-page-subtitle">{t('notifDiagnostics.subtitle')}</p>
      </div>
      <div className="ui-page-toolbar">
        <ListToolbar
          title={null}
          subtitle={null}
          filters={[
            <input
              key="userId"
              type="text"
              className="ui-input"
              placeholder={t('notifDiagnostics.userIdFilterPlaceholder')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />,
            <input
              key="dedupeKey"
              type="text"
              className="ui-input"
              placeholder={t('notifDiagnostics.dedupeKeyFilterPlaceholder')}
              value={dedupeKey}
              onChange={(e) => setDedupeKey(e.target.value)}
            />,
            <select
              key="notifType"
              className="ui-input"
              aria-label={t('notifDiagnostics.notifTypeFilterPlaceholder')}
              value={notifType}
              onChange={(e) => setNotifType(e.target.value)}
            >
              <option value="">{t('notifDiagnostics.notifTypeAll')}</option>
              {NOTIF_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>,
            <input
              key="from"
              type="date"
              className="ui-input"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            />,
            <input
              key="to"
              type="date"
              className="ui-input"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            />,
            <button key="clear" type="button" className="ui-button ghost" onClick={handleClearFilters}>
              {t('audit.clearFilters')}
            </button>
          ]}
          actions={(
            <button type="button" className="ui-button primary" onClick={handleSearch}>
              {t('notifDiagnostics.search')}
            </button>
          )}
        />
      </div>
      <div className="ui-page-content">
        <div className="sa-card sa-audit-list-card">
          {loading ? (
            <div className="sa-loading">{t('common.loading')}</div>
          ) : groups.length === 0 ? (
            <div className="sa-empty">{t('notifDiagnostics.empty')}</div>
          ) : (
            <>
              <div className="sa-audit-list">
                {groupsWithDayHeaders.map((g) => (
                  g.isDayHeader ? (
                    <div key={`day-${g.dayKey}`} className="sa-audit-day-header">
                      {g.label}
                    </div>
                  ) : (
                  <div key={g.dedupeKey} className="sa-audit-item" style={{ display: 'block' }}>
                    <div className="sa-audit-header">
                      <div>
                        <div className="sa-audit-action">
                          {g.dedupeKey?.startsWith('(sin-dedupe)-')
                            ? <em>{t('notifDiagnostics.noDedupeKey')}</em>
                            : g.dedupeKey}
                        </div>
                        <div className="sa-audit-actor">
                          {t('notifDiagnostics.user')} {g.userId}
                        </div>
                      </div>
                    </div>
                    <div className="sa-audit-detail-panel">
                      <div className="sa-audit-meta">
                        <strong>{t('notifDiagnostics.backendEvents')}</strong>
                        {(!g.backendEvents || g.backendEvents.length === 0) ? (
                          <div className="sa-empty">{t('notifDiagnostics.noBackendEvents')}</div>
                        ) : (
                          <ul>
                            {g.backendEvents.map((e, i) => (
                              <li key={i}>
                                {e.sentAt ? formatDateTime(e.sentAt) : '—'} — {e.type} —{' '}
                                {e.success ? (
                                  <span className="sa-badge sa-badge--success">OK</span>
                                ) : (
                                  <span className="sa-badge sa-badge--error">
                                    {t('notifDiagnostics.failed')} ({e.errorMessage || '—'})
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        <strong>{t('notifDiagnostics.clientEvents')}</strong>
                        {(!g.clientEvents || g.clientEvents.length === 0) ? (
                          <div className="sa-empty">{t('notifDiagnostics.noClientEvents')}</div>
                        ) : (
                          <ul>
                            {g.clientEvents.map((e, i) => (
                              <li key={i}>
                                {e.receivedAt ? formatDateTime(e.receivedAt) : '—'} — {e.event} —{' '}
                                {t('notifDiagnostics.type')}={e.notifType}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                  )
                ))}
              </div>
              {pagination.total > 0 && (
                <div className="sa-pagination">
                  <button
                    type="button"
                    className="sa-btn-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    {t('common.previous')}
                  </button>
                  <span className="sa-pagination-info">
                    {t('common.page')} {page} {t('common.of')} {totalPages} · {pagination.total} {t('common.results')}
                  </span>
                  <button
                    type="button"
                    className="sa-btn-secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
